import type { CheckoutData, PaymentData } from '@leanpay/models'
import { VendorRequestType } from '@leanpay/models'
import { Environment, getCountryCodeBookId, getDefaultAppLanguage, Market } from '@leanpay/utils'

// START COMMON CONSTANTS
const getMarketFromHostname = (): Market => {
    const hostname = window.location.hostname

    if (hostname.includes('.leanpay.ro') || hostname.includes('rou')) {
        return Market.ROM
    }

    if (hostname.includes('.leanpay.si') || hostname.includes('slo')) {
        return Market.SLO
    }

    if (hostname.includes('.leanpay.hu') || hostname.includes('hun')) {
        return Market.HUN
    }

    throw new Error(`[MARKET] Unknown hostname: ${hostname}`)
}
const isPrivateIp =
    /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(
        window.location.hostname,
    )
const isRunningOnLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    isPrivateIp ||
    (window.location.hostname.includes('leanpay') && !window.location.hostname.includes('.'))

export const currentMarket = isRunningOnLocalhost
    ? (import.meta.env.VITE_MARKET as Market)
    : getMarketFromHostname()

export const isMarketSlo = currentMarket === Market.SLO
export const isMarketRom = currentMarket === Market.ROM
export const isMarketHun = currentMarket === Market.HUN

const getGtmId = () => {
    if (isMarketSlo) {
        return 'GTM-M3G2LTW'
    }
    if (isMarketRom) {
        return 'GTM-NS79LZF'
    }
    if (isMarketHun) {
        return 'GTM-TK88QW62'
    }

    return ''
}

export const gtmId = getGtmId()

const getEnvironmentFromHostname = () => {
    const hostname = window.location.hostname

    if (isRunningOnLocalhost) {
        return Environment.LOCAL
    }

    if (hostname.includes('-dev-') || hostname.includes('-test-')) {
        return Environment.TILT
    }

    if (hostname.includes('test-checkout')) {
        return Environment.TEST
    }

    if (hostname.includes('taf-checkout')) {
        return Environment.TILT
    }
    if (hostname.includes('stage-checkout')) {
        return Environment.STAGE
    }

    if (hostname.includes('checkout')) {
        return Environment.PROD
    }

    throw new Error(`[ENV] Unknown hostname: ${hostname}`)
}

export const currentEnvironment = getEnvironmentFromHostname()

export const isLocalEnvironment = currentEnvironment === Environment.LOCAL
export const isTiltEnvironment = currentEnvironment === Environment.TILT
export const isTestEnvironment = currentEnvironment === Environment.TEST
export const isStageEnvironment = currentEnvironment === Environment.STAGE
export const isProdEnvironment = currentEnvironment === Environment.PROD
export const isNonProdLiveEnvironment = !isProdEnvironment && !isLocalEnvironment

export const defaultAppLanguage = getDefaultAppLanguage(currentMarket)
export const countryCodebookId = getCountryCodeBookId(currentMarket)

// END COMMON CONSTANTS

export enum CacheKey {
    KYCStatus = 'KYCStatus',
    StateMachineStatus = 'StateMachineStatus',
    CheckoutStateMachineStatus = 'CheckoutStateMachineStatus',
    Nationality = 'Nationality',
    FinancialData = 'FinancialData',
    Settlement = 'Settlement',
    PaymentData = 'PaymentData',
    DownloadInitialOffer = 'DownloadInitialOffer',
    SystemConfig = 'SystemConfig',
    PEPQuestionnaire = 'PEPQuestionnaire',
    PepStatus = 'PepStatus',
    SessionInfo = 'SessionInfo',
    CreditCards = 'CreditCards',
    CreditCardStatus = 'CreditCardStatus',
    GlobalDocument = 'GlobalDocument',
    FinancialDetails = 'FinancialDetails',
    AddressDetails = 'AddressDetails',
    KYCCheck = 'KYCCheck',
    UpsellOfferState = 'UpsellOfferState',
}

// How long the POS final screen keeps the loader while the state machine sits in
// the SAME non-final state before falling back to the check-type pending screen.
// Resets on every state transition, so only a genuine in-session stall trips it
// (e.g. an out-of-duty SISBON check that never resolves this session).
export const POS_DECISION_STALL_TIMEOUT_MS = 60_000

// State machine state meaning the decision was already confirmed → redirect out.
export const STATE_MACHINE_TERMINATED = 'TERMINATED'

// Poll cadence for the upsell offer-state endpoint while the offer is still being evaluated.
export const UPSELL_OFFER_POLL_INTERVAL = 3000

// Secondary "MAYBE LATER" CTA targets on the GPL upsell hook screen (per market).
export const MAYBE_LATER_URL_ROM = 'https://www.leanpay.ro/credit-online/'
export const MAYBE_LATER_URL_SLO = 'https://www.leanpay.si/gotovinski-kredit/'

// Analytics action sent in the record-step payload to distinguish the upsell hook CTAs.
export enum UpsellCtaAction {
    CheckOffer = 'UPSELL_CHECK_OFFER',
    MaybeLater = 'UPSELL_MAYBE_LATER',
}

export enum CheckoutRoute {
    ChoseInstallmentPlan = 'choose-installment',
    RequestOtp = 'request-otp',
    Login = 'login',
    PersonalDetails = 'personal-details',
    FinancialDetails = 'financial-details',
    PreviewFinancialDetails = 'preview-financial-details',
    AddressDetails = 'address-details',
    TwoFaPin = 'two-fa-pin',
    ForgotTwoFaPin = 'forgot-two-fa-pin',
    OfferConfirmation = 'offer-confirmation',
    SigContract = 'sign-contract',
    CheckoutError = 'checkout-error',
    CheckoutSuccess = 'checkout-success',
    VerificationInfo = 'verification-info',
    KYCVerification = 'kyc-verification',
    KYCVerificationSuccess = 'kyc-verification-success',
    ValidateOffer = 'validate-offer',
    Payment = 'payment',
    PaymentStatus = 'payment-status',
    PepQuestionnaire = 'pep-questionnaire',
    AisRequest = 'ais-request',
    AisStatus = 'ais-status',
}

const getTiltDashboardUrl = (): string => {
    const { protocol, hostname } = window.location
    const dashboardHostname = hostname.replace('checkout', 'app')
    return `${protocol}//${dashboardHostname}/dashboard`
}

const getTiltPQUrl = (): string => {
    const { protocol, hostname } = window.location
    const pqHostname = hostname.replace('checkout', 'pre-qualify')
    const returnUrl = `${protocol}//${hostname}/vendor-mock`
    return `${protocol}//${pqHostname}/?vendor=3ER7JP&returnUrl=${returnUrl}`
}

export const getDashboardUrl = (): string => {
    switch (currentMarket) {
        case Market.HUN:
            if (isProdEnvironment) {
                return 'https://app.leanpay.hu/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.hu/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.hu/dashboard'
            }
            if (isTiltEnvironment) {
                return getTiltDashboardUrl()
            }
            return 'http://localhost:4200/dashboard'
        case Market.ROM:
            if (isProdEnvironment) {
                return 'https://app.leanpay.ro/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.ro/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.ro/dashboard'
            }
            if (isTiltEnvironment) {
                return getTiltDashboardUrl()
            }
            return 'http://localhost:4200/dashboard'
        case Market.SLO:
            if (isProdEnvironment) {
                return 'https://app.leanpay.si/dashboard'
            }
            if (isTestEnvironment) {
                return 'https://test-app.leanpay.si/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.si/dashboard'
            }
            if (isTiltEnvironment) {
                return getTiltDashboardUrl()
            }
            return 'http://localhost:4200/dashboard'
        default:
            return 'http://localhost:4200/dashboard'
    }
}

export interface BackDestination {
    url: string
    isCustom: boolean
}

// CUSTOM (dashboard-originated / custom-remote) offers have no vendor successUrl, so the
// back CTA must return to the MyLeanpay dashboard. All other request types (WEB, VIRTUAL_CARD,
// in-store) return to the vendor successUrl. Mirrors the branching in CheckoutPendingSLO.
export const resolveBackDestination = (
    checkoutData: CheckoutData | null,
    paymentData: PaymentData | null,
): BackDestination => {
    const isCustom = checkoutData?.vendorRequestType === VendorRequestType.CUSTOM

    return {
        url: isCustom ? getDashboardUrl() : (paymentData?.successUrl ?? ''),
        isCustom,
    }
}

export const getPQUrl = (): string => {
    switch (currentMarket) {
        case Market.SLO:
            if (isProdEnvironment) {
                return 'https://pre-qualify.leanpay.si/?vendor=3ER7JP'
            }
            if (isTestEnvironment) {
                return 'https://test-pre-qualify.leanpay.si/?vendor=3ER7JP&returnUrl=http://test-checkout.leanpay.si/vendor-mock'
            }
            if (isStageEnvironment) {
                return 'https://stage-pre-qualify.leanpay.si/?vendor=3ER7JP&returnUrl=http://stage-checkout.leanpay.si/vendor-mock'
            }
            if (isTiltEnvironment) {
                return getTiltPQUrl()
            }
            return 'http://localhost:4303/?vendor=3ER7JP&returnUrl=http://localhost:4301/vendor-mock'

        case Market.ROM:
            if (isProdEnvironment) {
                return 'https://pre-qualify.leanpay.ro/?vendor=3ER7JP'
            }
            if (isTestEnvironment) {
                return 'https://test-pre-qualify.leanpay.ro/?vendor=3ER7JP&returnUrl=http://test-checkout.leanpay.ro/vendor-mock'
            }
            if (isStageEnvironment) {
                return 'https://stage-pre-qualify.leanpay.ro/?vendor=3ER7JP&returnUrl=http://stage-checkout.leanpay.ro/vendor-mock'
            }
            if (isTiltEnvironment) {
                return getTiltPQUrl()
            }

            return 'http://localhost:4303/?vendor=3ER7JP&returnUrl=http://localhost:4301/vendor-mock'

        default:
            return 'http://localhost:4303/?vendor=3ER7JP&returnUrl=http://localhost:4301/vendor-mock'
    }
}

//Steps for validating offer for ROM market
export enum ValidateOfferStepsROM {
    IDENTITY_VERIFICATION = 'identityVerification',
    FINANCIAL_DATA_CONFIRMATION = 'financialDataConfirmation',
    OFFER_VALIDATION = 'offerValidation',
    SCORING_ANALYSIS = 'scoringAnalysis',
}

export const shouldSkipKYC =
    import.meta.env.VITE_MARKET === Market.ROM && import.meta.env.VITE_SKIP_KYC === 'true'
